import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import moment, { Moment } from "moment";
import { CloseButton, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Button from "@/component/Button";
import { warningToast } from "@/component/Toast";
import InViewObserver from "@/component/InViewObserver";

import { DateRange, StrictDateRange, TimeValue } from "./type";
import {
    adjustSameMonth,
    cloneDateRange,
    dateRangeInvalidCause,
    getDateRangeInDayOrder,
    getFormattedDate
} from "./helper";
import Calendar from "./component/Calendar";
import TimeInput from "./component/TimeInput";
import DateInput from "./component/DateInput";


type DateRangePickerProps = {
    min?: number;
    max?: number;
    useTime?: boolean;
    useFuture?: boolean;
    disabled?: boolean;
    period: DateRange;
    onChangePeriod: (period: StrictDateRange) => void;
    children?: React.ReactNode;
};

const DateRangePicker: React.FC<DateRangePickerProps> = (props) => {
    const {
        period,
        min, max,
        useTime = false,
        useFuture = false,
        disabled = false,
        onChangePeriod,
        children,
    } = props;

    const intl = useIntl();

    const [calendarMonths, setCalendarMonths] = useState<StrictDateRange>();
    const [hoveredDate, setHoveredDate] = useState<Moment | undefined>(undefined);
    const [selectedDates, setSelectedDates] = useState<DateRange>({
        start: undefined,
        end: undefined,
    });
    const [selectedTimes, setSelectedTimes] = useState<{ start: TimeValue; end: TimeValue; }>({
        start: { hour: 0, minute: 0 },
        end: { hour: 23, minute: 59 },
    });

    const init = useCallback(() => {
        const initMonths = {
            start: (period.start ?? moment()).clone().startOf('month'),
            end: (period.end ?? moment()).clone().startOf('month'),
        };
        setCalendarMonths(adjustSameMonth(initMonths, 'backward'));

        setSelectedDates(cloneDateRange(period));
        setSelectedTimes({
            start: { hour: period.start?.hour() ?? 0, minute: period.start?.minute() ?? 0 },
            end: { hour: period.end?.hour() ?? 23, minute: period.end?.minute() ?? 59 }
        });
    }, [period]);

    const validate = useCallback((dateRange: DateRange): dateRange is StrictDateRange => {
        return dateRangeInvalidCause(dateRange, min, max) === undefined;
    }, [min, max]);

    const warnToast = useCallback((dateRange: DateRange) => {
        switch (dateRangeInvalidCause(dateRange, min, max)) {
            case "max":
                warningToast(intl.formatMessage({ id: "i00017" }, { max }));
                break;
            case "min":
                warningToast(intl.formatMessage({ id: "i00031" }, { min }));
                break;
            default:
                break;
        }
    }, [intl, max, min]);

    const onClickDate = useCallback((clickedDate: Moment) => {
        setSelectedDates(prev => {
            const { start, end } = prev;
            const range = (start && !end)
                ? getDateRangeInDayOrder(start, clickedDate)
                : { start: clickedDate, end: undefined };
            if (!validate(range)) {
                warnToast(range);
            }
            return range;
        });
    }, [validate, warnToast]);

    const onChangeMonth = useCallback((pos: 'start' | 'end', targetMonth: Moment) => {
        if (!calendarMonths) {
            return;
        }
        const monthsClone = cloneDateRange(calendarMonths);
        monthsClone[pos] = targetMonth.clone().startOf('month');

        setCalendarMonths(adjustSameMonth(monthsClone, pos === 'start' ? 'forward' : 'backward'));
    }, [calendarMonths]);

    const onChangeTextInput = useCallback((pos: 'start' | 'end', date: Moment) => {
        const dateClone = date.clone();
        const selectedDatesClone = cloneDateRange(selectedDates);
        selectedDatesClone[pos] = dateClone;

        if (selectedDatesClone.start && selectedDatesClone.end) {
            setSelectedDates(getDateRangeInDayOrder(selectedDatesClone.start, selectedDatesClone.end));
        } else {
            setSelectedDates(selectedDatesClone);
        }
        setSelectedTimes(prev => ({
            ...prev,
            [pos]: { hour: dateClone.hour(), minute: dateClone.minute() }
        }));
    }, [selectedDates]);

    const onClickSubmitBtn = (e: React.MouseEvent) => {
        if (!validate(dateRangeWithTime)) {
            e.preventDefault();
            return;
        }
        onChangePeriod(dateRangeWithTime);
    };

    const dateRangeWithTime = useMemo(() => {
        const clone = cloneDateRange(selectedDates);
        return {
            start: clone.start?.set(selectedTimes.start).startOf('minute'),
            end: clone.end?.set(selectedTimes.end).endOf('minute'),
        };
    }, [selectedDates, selectedTimes]);

    const activeDateRange = useMemo(() => {
        if (!hoveredDate) {
            return selectedDates;
        }
        const { start, end } = cloneDateRange(selectedDates);

        if (!start && !end) {
            return { start: hoveredDate, end: undefined };
        } else if (start && !end) {
            return getDateRangeInDayOrder(start, hoveredDate);
        } else {
            return selectedDates;
        }
    }, [hoveredDate, selectedDates]);

    useEffect(() => {
        init();
    }, [init]);

    if (!calendarMonths) {
        return null;
    }
    return (
        <Popover>{({ open, close }) => (<>
            <InViewObserver enabled={open}
                            onHidden={close}
            >{inViewRef => (
                <PopoverButton as="div"
                               ref={inViewRef}
                               disabled={disabled}
                               onClick={init}
                               aria-haspopup="dialog"
                               aria-expanded={open}
                >
                    {children || (
                        <Button size="md"
                                variant="outline-primary"
                                className={classNames(
                                    "!h-[39px] mt-px p-2.5 flex items-center justify-between",
                                    "text-[14px] font-[400] leading-none whitespace-nowrap",
                                    useTime ? "w-[274px]" : "w-[164px]"
                                )}
                        >
                            <div>{getFormattedDate(period.start, useTime)}</div>
                            <div>~</div>
                            <div>{getFormattedDate(period.end, useTime)}</div>
                        </Button>
                    )}
                </PopoverButton>
            )}</InViewObserver>
            <PopoverPanel transition
                          role="dialog"
                          aria-label="기간 선택"
                          anchor={{ to: "bottom start", gap: 8 }}
                          data-testid="date-picker-panel"
                          className={classNames(
                              "px-4 pt-3 pb-5 flex flex-col bg-white border border-black-200 rounded-[6px] z-[999]",
                              "transition duration-200 ease-in-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
                          )}
            >
                <div className="flex gap-2">
                    <div className="flex flex-col">
                        <Calendar useFuture={useFuture}
                                  month={calendarMonths.start}
                                  activeDateRange={activeDateRange}
                                  onSelect={onClickDate}
                                  onHover={setHoveredDate}
                                  onChangeMonth={month => onChangeMonth('start', month)}
                        />
                        {useTime && (
                            <div className="ml-4.5 mt-4">
                                <TimeInput value={selectedTimes.start}
                                           aria-label="시작 시간"
                                           onChange={time => setSelectedTimes(prev => ({ ...prev!, start: time }))}
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <Calendar useFuture={useFuture}
                                  month={calendarMonths.end}
                                  activeDateRange={activeDateRange}
                                  onSelect={onClickDate}
                                  onHover={setHoveredDate}
                                  onChangeMonth={month => onChangeMonth('end', month)}
                        />
                        {useTime && (
                            <div className="ml-4.5 mt-4">
                                <TimeInput value={selectedTimes.end}
                                           aria-label="종료 시간"
                                           onChange={time => setSelectedTimes(prev => ({ ...prev!, end: time }))}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex items-center">
                    {!!(max || min) && (
                        <div className="text-black-400 text-[12px] font-[400]">
                            {max
                                ? intl.formatMessage({ id: "i00017" }, { max })
                                : intl.formatMessage({ id: "i00031" }, { min })
                            }
                        </div>
                    )}
                    <div className="ml-auto flex gap-2">
                        <div className="flex items-center gap-1">
                            <DateInput useTime={useTime}
                                       useFuture={useFuture}
                                       aria-label="시작일"
                                       value={dateRangeWithTime.start?.clone()}
                                       onInputValidDate={date => onChangeTextInput('start', date)}
                            />

                            <div className="w-2 text-chat42 text-[13px] font-[400]">
                                ~
                            </div>

                            <DateInput useTime={useTime}
                                       useFuture={useFuture}
                                       aria-label="종료일"
                                       value={dateRangeWithTime.end?.clone()}
                                       onInputValidDate={date => onChangeTextInput('end', date)}
                            />
                        </div>
                        <CloseButton as="div">
                            <Button size="xs"
                                    variant="primary"
                                    className="px-4 py-1 whitespace-nowrap"
                                    disabled={!validate(dateRangeWithTime)}
                                    onClick={onClickSubmitBtn}
                            >
                                {intl.formatMessage({ id: "b0001" })}
                            </Button>
                        </CloseButton>
                    </div>
                </div>
            </PopoverPanel>
        </>)}</Popover>
    );
};

export default DateRangePicker;
